// Cálculo de vencimiento del QR de un pedido, compartido entre MyOrdersPage,
// MyOrdersCompanyPage y ShareOrderModal (antes duplicado con un criterio
// distinto en cada página — QA #: "QR vencido pero se puede compartir").
//
// Criterio de negocio (explícito a propósito, es sensible):
//   `pedido_vigente` es la ÚLTIMA fecha en que el QR es válido (formato
//   "dd-mm-yyyy"). Se compara contra "hoy" a nivel de día (sin horas).
//   - diasRestantes > 0  -> vigente, vence en el futuro.
//   - diasRestantes === 0 -> vigente HOY todavía ("QR vence hoy"): el texto ya
//     mostrado en MyOrdersPage confirma esta intención (avisa que hoy es el
//     último día, no que ya venció).
//   - diasRestantes < 0  -> vencido: la fecha límite ya pasó.
//   Por lo tanto `estaVencido()` es estrictamente `diasRestantes < 0`.
//   MyOrdersCompanyPage usaba `<= 0` (trataba "vence hoy" como vencido): esa
//   era la inconsistencia real a corregir, no una diferencia intencional.
//
// `pedido_vigente` ausente o con un formato que no se pueda parsear como
// "dd-mm-yyyy" válido se trata como "no vencido" (fail-open): no hay
// suficiente información para bloquear una acción sobre un pedido que podría
// seguir siendo válido, y así se comportaba ya calcularDiasRestantes en
// MyOrdersPage (devolvía "" y no mostraba "QR vencido"). Nunca lanza.

const parseFechaVigente = (pedido_vigente) => {
  if (typeof pedido_vigente !== "string") return null;

  const partes = pedido_vigente.split("-");
  if (partes.length !== 3) return null;

  const [dd, mm, yyyy] = partes.map(Number);
  if (![dd, mm, yyyy].every(Number.isFinite)) return null;

  const fecha = new Date(yyyy, mm - 1, dd);
  // Descarta fechas "desbordadas" que Date normaliza silenciosamente
  // (ej. 31-02-2026 -> 3 de marzo), en vez de aceptar un dato corrupto.
  if (
    fecha.getFullYear() !== yyyy ||
    fecha.getMonth() !== mm - 1 ||
    fecha.getDate() !== dd
  ) {
    return null;
  }

  fecha.setHours(0, 0, 0, 0);
  return fecha;
};

// Devuelve los días restantes hasta el vencimiento (puede ser negativo si ya
// venció), o `null` si `pedido_vigente` está ausente o no se pudo parsear.
export const calcularDiasRestantes = (pedido_vigente) => {
  const fechaVencimiento = parseFechaVigente(pedido_vigente);
  if (fechaVencimiento === null) return null;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  return Math.floor(
    (fechaVencimiento.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24),
  );
};

// true solo cuando se pudo determinar con certeza que la fecha límite ya
// pasó. Dato ausente/inválido -> false (fail-open, ver nota arriba).
export const estaVencido = (pedido_vigente) => {
  const dias = calcularDiasRestantes(pedido_vigente);
  return dias !== null && dias < 0;
};

// Texto ya formateado para la columna "Fecha" de ambas páginas de pedidos.
export const textoVigenciaQr = (pedido_vigente) => {
  const dias = calcularDiasRestantes(pedido_vigente);
  if (dias === null) return "";
  if (dias < 0) return "QR vencido";
  if (dias === 0) return "QR vence hoy";
  if (dias === 1) return "QR vence en 1 día";
  return `QR vence en ${dias} días`;
};
